// @flow
import type { ShippingOption } from '../auction/types'
import BidItem from '../../ui/views/bid-item'
import {
  compose,
  withStateHandlers,
  lifecycle,
  withHandlers,
  withProps
} from 'recompose'
import { connect } from 'react-redux'
import withTranslations from '../../core/withTranslations'
import withLoadingUntilProps from '../../core/withLoadingUntilProps'
import withErrorIfProps from '../../core/withErrorIfProps'
import {
  isUserProfileCompleted,
  isSessionLoaded,
  isLoggedIn,
  isEmailVerified
} from '../session'
import { fetchItem, getIsItemLoaded, getItemShippingOption } from '../item'
import { AUCTION_SHIPPING_OPTION } from '../auction/constants'
import {
  getIsUserBidBigEnough,
  getCanUserBidDecrease,
  getIsInitialBid,
  getMinUserBid,
  getMinIncrementAmount,
  getMinIncrementPercentageText
} from './utils'
import { makeBid } from './actions'
import { getItem } from '../selectors'
import {
  MIN_BID_INCREMENT_PERCENTAGE,
  LIMIT_MIN_BID_INCREMENT_AMOUNT
} from './index'
import { isAuctionLive } from '../auction/utils'

const mapStateToProps = (state, props) => {
  const itemId = props.match.params.id
  const item = itemId && getItem(itemId)(state)
  const isItemLoaded = itemId && getIsItemLoaded(itemId, state)
  const shippingOption = itemId && getItemShippingOption(itemId)(state)
  const minIncrementAmount =
    item &&
    getMinIncrementAmount(
      item,
      MIN_BID_INCREMENT_PERCENTAGE,
      LIMIT_MIN_BID_INCREMENT_AMOUNT
    )
  const minUserBid =
    item && minIncrementAmount && getMinUserBid(item, minIncrementAmount)
  return {
    itemId,
    item,
    isAuctionLive: item && isAuctionLive(item.auction),
    isSessionLoaded: isSessionLoaded(state),
    isLoggedIn: isLoggedIn(state),
    isItemLoaded,
    isUserProfileCompleted: isUserProfileCompleted(state),
    isEmailVerified: isEmailVerified(state),
    shippingOption,
    currentBidAmount: item && item.currentBidAmount,
    initialBid: item && item.initialBid,
    isInitialBid: !!item && getIsInitialBid(item),
    minIncrementAmount,
    limitMinIncrementAmount: LIMIT_MIN_BID_INCREMENT_AMOUNT,
    minIncrementPercentageText: getMinIncrementPercentageText(
      MIN_BID_INCREMENT_PERCENTAGE
    ),
    minUserBid,
    firstBidShortcutAmount: minUserBid,
    secondBidShortcutAmount: minUserBid + minIncrementAmount,
    thirdBidShortcutAmount: minUserBid + minIncrementAmount * 2
  }
}
const withUserBidAndItemData = connect(mapStateToProps, { makeBid, fetchItem })

export const withSecurityCompleteProfileAndFetchOnMount = lifecycle({
  componentDidMount() {
    const {
      isLoggedIn,
      isUserProfileCompleted,
      isEmailVerified,
      isItemLoaded,
      itemId,
      history,
      location,
      fetchItem
    } = this.props
    if (!isLoggedIn)
      return history.replace('/login', { returnUrl: location.pathname })
    if (isLoggedIn && !isEmailVerified) {
      return history.replace('/verify-failed')
    }
    if (!isUserProfileCompleted)
      return history.replace('/myprofile/complete-profile')
    if (!isItemLoaded) return fetchItem(itemId)
  }
})

const makeInitialState = props => {
  const { minUserBid } = props
  return {
    isBetting: false,
    userBid: minUserBid
  }
}

export const withBidState = withStateHandlers(
  props => makeInitialState(props),
  {
    setUserBid: state => (value: number) => ({ userBid: value }),
    setIsBetting: state => (value: boolean) => ({ isBetting: value }),
    increaseUserBid: (state, props) => () => {
      const { userBid } = state
      const { minUserBid } = props
      return getIsUserBidBigEnough(minUserBid, userBid)
        ? { userBid: userBid + 1 }
        : { userBid: minUserBid }
    },
    decreaseUserBid: state => () => ({
      userBid: state.userBid - 1
    }),
    setFirstIncrementUserBid: (state, props) => () => ({
      userBid: props.firstBidShortcutAmount
    }),
    setSecondIncrementUserBid: (state, props) => () => ({
      userBid: props.secondBidShortcutAmount
    }),
    setThirdIncrementUserBid: (state, props) => () => ({
      userBid: props.thirdBidShortcutAmount
    })
  }
)

export const withButtonStates = withProps(props => {
  const { userBid, minUserBid, isBetting, isAuctionLive } = props
  return {
    isDecreaseButtonDisabled:
      !userBid || isBetting || !getCanUserBidDecrease(minUserBid, userBid),
    isBidButtonDisabled:
      !userBid ||
      isBetting ||
      !getIsUserBidBigEnough(minUserBid, userBid) ||
      !isAuctionLive
  }
})

export const withBidHandlers = withHandlers({
  handleClose: props => e => {
    e.preventDefault()
    props.history.goBack()
  },
  handleAmountChange: props => e => {
    e.preventDefault()
    const value = Number(e.target.value)
    props.setUserBid(value)
  },
  handleBid: props => e => {
    e.preventDefault()
    const {
      makeBid,
      itemId,
      userBid,
      minUserBid,
      setIsBetting,
      history
    } = props
    if (getIsUserBidBigEnough(minUserBid, userBid)) {
      setIsBetting(true)
      makeBid(Number(itemId), userBid)
        .then(() => {
          history.goBack()
        })
        .catch(error => {
          console.warn('error bidding', error)
          setIsBetting(false)
        })
    }
  }
})

export const selectShippingChargeText = (
  shippingOption: ShippingOption
): string | void => {
  switch (shippingOption) {
    case AUCTION_SHIPPING_OPTION.NATIONAL:
      return 'app:auction.optionsForm.shippingNational'
    case AUCTION_SHIPPING_OPTION.WORLDWIDE:
      return 'app:auction.optionsForm.shippingWorldwide'
    case AUCTION_SHIPPING_OPTION.NONE:
      return 'app:auction.optionsForm.shippingNone'
    default:
      break
  }
}

const translations = withTranslations(props => {
  let translations = {
    makeABidText: 'app:item.bids.makeABid',
    currentBidIsText: 'app:item.bids.currentBidIs',
    initialBidIsText: 'app:item.bids.initialBidIs',
    minimumIncrementIsText: 'app:item.bids.minimumIncrementIs',
    addAutomaticBidText: 'app:item.bids.addAutomaticBid',
    ofInitialBidText: 'app:item.bids.ofInitialBid',
    upToAMaximumOfText: 'app:item.bids.upToMaximumOf',
    bidText: 'common:bid',
    cancelText: 'common:cancel'
  }
  if (props.shippingOption) {
    translations = {
      ...translations,
      shippingChargeText: selectShippingChargeText(props.shippingOption)
    }
  }
  return { ...translations }
})

export const BidItemEnhancer = compose(
  withUserBidAndItemData,
  withLoadingUntilProps('isSessionLoaded'),
  withSecurityCompleteProfileAndFetchOnMount,
  withLoadingUntilProps('isItemLoaded'),
  withErrorIfProps(props => props.isItemLoaded && !props.item),
  withBidState,
  withButtonStates,
  withBidHandlers,
  translations
)

export default BidItemEnhancer(BidItem)



// WEBPACK FOOTER //
// ./src/services/bid/BidItem.js